import React, { useState, useEffect, useCallback } from 'react';
import {
  LayoutDashboard, Cpu, HardDrive, Network, MemoryStick, Server, AlertCircle, CheckCircle2, XCircle, Settings, RefreshCw, TrendingUp, TrendingDown, Bell, Clock, Filter, Search, Eye, EyeOff, Edit, Save, Plus, Trash2, Info, HelpCircle, ExternalLink, Copy, Globe, Database, Mail, Phone, MessageSquare, Heart, Award, Target, Timer, Brain, Route, Zap, MapPin, FileText, File, FileVideo, FileAudio, FileImage, FileSpreadsheet, FilePdf, FileZip, FileCheck, FileX, FilePlus, FileMinus, FileEdit, FileSearch, FileSlash, FileWarning, FileQuestion, FileHeart, FileClock, FileUp, FileDown, FileLeft, FileRight, FileBarChart, FilePieChart, FileLineChart, FileScissors, FileType, FileSymlink, FileStack, FileTree, FileBinary, FileCog, FileSettings, FileLock, FileUnlock, FileKey, FileSignature, FileVerified, FileBadge, FileTag, FileLabel, FileBookmark, FileStar, FileFlag, FileAlert, FileInfo, FileHelp, FileMessage, FileComment, FileChat, FileMail, FilePhone, Users, Activity, BarChart3, PieChart, LineChart, ScrollText
} from 'lucide-react';
import { AdminService } from '../../services/AdminService';

const PerformanceOptimization = () => {
  const [performanceMetrics, setPerformanceMetrics] = useState(null);
  const [optimizationRecommendations, setOptimizationRecommendations] = useState([]);
  const [resourceUsage, setResourceUsage] = useState(null);
  const [performanceHistory, setPerformanceHistory] = useState(null);
  const [optimizationConfig, setOptimizationConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('24h');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const loadPerformanceData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const metricsResponse = await AdminService.getPerformanceMetrics(timeRange);
      const recommendationsResponse = await AdminService.getOptimizationRecommendations();
      const resourceResponse = await AdminService.getResourceUsage(timeRange);
      const historyResponse = await AdminService.getPerformanceHistory(timeRange);
      const configResponse = await AdminService.getOptimizationConfig();

      if (metricsResponse.success && recommendationsResponse.success && resourceResponse.success && historyResponse.success && configResponse.success) {
        setPerformanceMetrics(metricsResponse.data);
        setOptimizationRecommendations(recommendationsResponse.data);
        setResourceUsage(resourceResponse.data);
        setPerformanceHistory(historyResponse.data);
        setOptimizationConfig(configResponse.data);
      } else {
        setError(metricsResponse.message || recommendationsResponse.message || resourceResponse.message || historyResponse.message || configResponse.message);
      }
    } catch (err) {
      setError('Failed to fetch performance optimization data.');
      console.error('Error fetching performance optimization data:', err);
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    loadPerformanceData();
    const interval = setInterval(loadPerformanceData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [loadPerformanceData]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'optimal': return 'text-green-600 bg-green-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      case 'critical': return 'text-red-600 bg-red-100';
      case 'low': return 'text-blue-600 bg-blue-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'medium': return 'text-purple-600 bg-purple-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getTrendIcon = (trend) => {
    if (trend === 'up') return <TrendingUp size={16} className="text-red-500" />;
    if (trend === 'down') return <TrendingDown size={16} className="text-green-500" />;
    return null;
  };

  const handleApplyOptimization = async (recommendationId) => {
    if (window.confirm('Are you sure you want to apply this optimization? This may affect system performance temporarily.')) {
      try {
        const response = await AdminService.applyOptimization(recommendationId);
        if (response.success) {
          alert('Optimization applied successfully!');
          loadPerformanceData();
        } else {
          alert(`Failed to apply optimization: ${response.message}`);
        }
      } catch (err) {
        alert('Error applying optimization.');
        console.error('Error applying optimization:', err);
      }
    }
  };

  const handleDismissRecommendation = async (recommendationId) => {
    if (window.confirm('Are you sure you want to dismiss this recommendation?')) {
      try {
        const response = await AdminService.dismissRecommendation(recommendationId);
        if (response.success) {
          alert('Recommendation dismissed successfully!');
          loadPerformanceData();
        } else {
          alert(`Failed to dismiss recommendation: ${response.message}`);
        }
      } catch (err) {
        alert('Error dismissing recommendation.');
        console.error('Error dismissing recommendation:', err);
      }
    }
  };

  const handleUpdateOptimizationConfig = async () => {
    if (window.confirm('Are you sure you want to update optimization configuration?')) {
      try {
        const response = await AdminService.updateOptimizationConfig(optimizationConfig);
        if (response.success) {
          alert('Optimization configuration updated successfully!');
          loadPerformanceData();
        } else {
          alert(`Failed to update configuration: ${response.message}`);
        }
      } catch (err) {
        alert('Error updating configuration.');
        console.error('Error updating configuration:', err);
      }
    }
  };

  const handleRunOptimizationScan = async () => {
    if (window.confirm('Are you sure you want to run a full optimization scan? This may take several minutes.')) {
      try {
        const response = await AdminService.runOptimizationScan();
        if (response.success) {
          alert('Optimization scan completed successfully!');
          loadPerformanceData();
        } else {
          alert(`Failed to run optimization scan: ${response.message}`);
        }
      } catch (err) {
        alert('Error running optimization scan.');
        console.error('Error running optimization scan:', err);
      }
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-500">Loading performance optimization data...</div>;
  }

  if (error) {
    return <div className="p-6 text-center text-red-500">Error: {error}</div>;
  }

  if (!performanceMetrics || !resourceUsage || !optimizationConfig) {
    return <div className="p-6 text-center text-gray-500">No performance optimization data available.</div>;
  }

  const filteredRecommendations = optimizationRecommendations.filter(rec => {
    const matchesFilter = categoryFilter === 'all' || rec.category === categoryFilter;
    const matchesSearch = searchTerm === '' ||
      rec.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rec.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rec.category.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center">
          <Zap className="mr-3 text-indigo-600" size={30} />
          Performance Optimization
        </h1>
        <div className="flex items-center space-x-4">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="1h">Last 1 Hour</option>
            <option value="6h">Last 6 Hours</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
          </select>
          <button
            onClick={handleRunOptimizationScan}
            className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-md shadow-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <Brain className="mr-2" size={18} /> Run Scan
          </button>
          <button
            onClick={loadPerformanceData}
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md shadow-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <RefreshCw className="mr-2" size={18} /> Refresh
          </button>
        </div>
      </div>

      <p className="text-gray-600 mb-8">
        Monitor and optimize system performance to ensure optimal operation and resource utilization.
        <span className="ml-2 text-sm text-gray-500">Last Updated: {new Date(performanceMetrics.lastUpdated).toLocaleString()}</span>
      </p>

      {/* Performance Overview Metrics */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <LayoutDashboard className="mr-2 text-purple-600" size={24} /> Performance Overview
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {performanceMetrics.overviewMetrics.map((metric, index) => (
            <div key={index} className="bg-white p-6 rounded-lg shadow-md border border-gray-200 flex items-center justify-between">
              <div>
                <p className="text-lg font-medium text-gray-600">{metric.name}</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{metric.value}</p>
                <div className="flex items-center mt-2 text-sm">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(metric.status)}`}>
                    {metric.status}
                  </span>
                  {metric.trend && <span className="ml-3">{getTrendIcon(metric.trend)}</span>}
                </div>
              </div>
              {metric.type === 'cpu_performance' && <Cpu size={40} className="text-blue-500 opacity-70" />}
              {metric.type === 'memory_performance' && <MemoryStick size={40} className="text-green-500 opacity-70" />}
              {metric.type === 'disk_performance' && <HardDrive size={40} className="text-orange-500 opacity-70" />}
              {metric.type === 'network_performance' && <Network size={40} className="text-purple-500 opacity-70" />}
            </div>
          ))}
        </div>
      </section>

      {/* Resource Usage Analysis */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <BarChart3 className="mr-2 text-blue-600" size={24} /> Resource Usage Analysis
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
            <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
              <Cpu className="mr-2 text-blue-500" size={20} /> CPU Performance
            </h3>
            {/* Placeholder for CPU Performance Chart */}
            <div className="h-48 flex items-center justify-center text-gray-400 bg-gray-50 rounded-md border border-dashed border-gray-300">
              <LineChart size={30} className="mr-2" /> CPU Performance Chart Placeholder
            </div>
            <ul className="mt-4 space-y-2 text-sm text-gray-600">
              {resourceUsage.cpuMetrics.map((metric, index) => (
                <li key={index} className="flex justify-between">
                  <span>{metric.name}:</span>
                  <span className="font-medium text-gray-800">{metric.value}%</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
            <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
              <MemoryStick className="mr-2 text-green-500" size={20} /> Memory Performance
            </h3>
            {/* Placeholder for Memory Performance Chart */}
            <div className="h-48 flex items-center justify-center text-gray-400 bg-gray-50 rounded-md border border-dashed border-gray-300">
              <BarChart3 size={30} className="mr-2" /> Memory Performance Chart Placeholder
            </div>
            <ul className="mt-4 space-y-2 text-sm text-gray-600">
              {resourceUsage.memoryMetrics.map((metric, index) => (
                <li key={index} className="flex justify-between">
                  <span>{metric.name}:</span>
                  <span className="font-medium text-gray-800">{metric.value}%</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
            <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
              <HardDrive className="mr-2 text-orange-500" size={20} /> Disk Performance
            </h3>
            {/* Placeholder for Disk Performance Chart */}
            <div className="h-48 flex items-center justify-center text-gray-400 bg-gray-50 rounded-md border border-dashed border-gray-300">
              <PieChart size={30} className="mr-2" /> Disk Performance Chart Placeholder
            </div>
            <ul className="mt-4 space-y-2 text-sm text-gray-600">
              {resourceUsage.diskMetrics.map((metric, index) => (
                <li key={index} className="flex justify-between">
                  <span>{metric.name}:</span>
                  <span className="font-medium text-gray-800">{metric.value} MB/s</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
            <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
              <Network className="mr-2 text-purple-500" size={20} /> Network Performance
            </h3>
            {/* Placeholder for Network Performance Chart */}
            <div className="h-48 flex items-center justify-center text-gray-400 bg-gray-50 rounded-md border border-dashed border-gray-300">
              <LineChart size={30} className="mr-2" /> Network Performance Chart Placeholder
            </div>
            <ul className="mt-4 space-y-2 text-sm text-gray-600">
              {resourceUsage.networkMetrics.map((metric, index) => (
                <li key={index} className="flex justify-between">
                  <span>{metric.name}:</span>
                  <span className="font-medium text-gray-800">{metric.value} KB/s</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Optimization Recommendations */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <Brain className="mr-2 text-green-600" size={24} /> Optimization Recommendations
        </h2>
        <div className="flex items-center space-x-4 mb-4">
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Categories</option>
            <option value="cpu">CPU Optimization</option>
            <option value="memory">Memory Optimization</option>
            <option value="disk">Disk Optimization</option>
            <option value="network">Network Optimization</option>
            <option value="database">Database Optimization</option>
            <option value="cache">Cache Optimization</option>
          </select>
          <div className="relative">
            <input
              type="text"
              placeholder="Search recommendations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Recommendation
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priority
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Impact
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Effort
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredRecommendations.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">No recommendations matching criteria.</td>
                </tr>
              ) : (
                filteredRecommendations.map((rec) => (
                  <tr key={rec.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{rec.title}</div>
                        <div className="text-sm text-gray-500">{rec.description}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{rec.category}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getPriorityColor(rec.priority)}`}>
                        {rec.priority}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{rec.impact}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{rec.effort}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button onClick={() => handleApplyOptimization(rec.id)} className="text-green-600 hover:text-green-900 mr-4">
                        <CheckCircle2 size={18} />
                      </button>
                      <button onClick={() => handleDismissRecommendation(rec.id)} className="text-red-600 hover:text-red-900">
                        <XCircle size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Performance History */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <Activity className="mr-2 text-indigo-600" size={24} /> Performance History
        </h2>
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Performance Trends</h3>
              {/* Placeholder for Performance Trends Chart */}
              <div className="h-64 flex items-center justify-center text-gray-400 bg-gray-50 rounded-md border border-dashed border-gray-300">
                <LineChart size={30} className="mr-2" /> Performance Trends Chart Placeholder
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Optimization Impact</h3>
              {/* Placeholder for Optimization Impact Chart */}
              <div className="h-64 flex items-center justify-center text-gray-400 bg-gray-50 rounded-md border border-dashed border-gray-300">
                <BarChart3 size={30} className="mr-2" /> Optimization Impact Chart Placeholder
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Optimization Configuration */}
      <section>
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <Settings className="mr-2 text-gray-600" size={24} /> Optimization Configuration
        </h2>
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="autoOptimization" className="block text-sm font-medium text-gray-700">Auto Optimization</label>
              <input
                type="checkbox"
                id="autoOptimization"
                checked={optimizationConfig.autoOptimization}
                onChange={(e) => setOptimizationConfig({ ...optimizationConfig, autoOptimization: e.target.checked })}
                className="mt-1 form-checkbox"
              />
            </div>
            <div>
              <label htmlFor="optimizationThreshold" className="block text-sm font-medium text-gray-700">Optimization Threshold (%)</label>
              <input
                type="number"
                id="optimizationThreshold"
                value={optimizationConfig.optimizationThreshold}
                onChange={(e) => setOptimizationConfig({ ...optimizationConfig, optimizationThreshold: parseInt(e.target.value) })}
                className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label htmlFor="scanInterval" className="block text-sm font-medium text-gray-700">Scan Interval (minutes)</label>
              <input
                type="number"
                id="scanInterval"
                value={optimizationConfig.scanInterval}
                onChange={(e) => setOptimizationConfig({ ...optimizationConfig, scanInterval: parseInt(e.target.value) })}
                className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label htmlFor="notificationEnabled" className="block text-sm font-medium text-gray-700">Notification Enabled</label>
              <input
                type="checkbox"
                id="notificationEnabled"
                checked={optimizationConfig.notificationEnabled}
                onChange={(e) => setOptimizationConfig({ ...optimizationConfig, notificationEnabled: e.target.checked })}
                className="mt-1 form-checkbox"
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="optimizationCategories" className="block text-sm font-medium text-gray-700">Optimization Categories</label>
              <div className="mt-1 flex items-center space-x-4">
                {optimizationConfig.optimizationCategories.map((category) => (
                  <label key={category} className="inline-flex items-center">
                    <input
                      type="checkbox"
                      className="form-checkbox"
                      checked={optimizationConfig.enabledCategories.includes(category)}
                      onChange={(e) => {
                        const enabledCategories = e.target.checked
                          ? [...optimizationConfig.enabledCategories, category]
                          : optimizationConfig.enabledCategories.filter(c => c !== category);
                        setOptimizationConfig({ ...optimizationConfig, enabledCategories });
                      }}
                    />
                    <span className="ml-2 text-sm text-gray-700">{category}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-6 flex justify-end">
            <button
              onClick={handleUpdateOptimizationConfig}
              className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md shadow-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <Save className="mr-2" size={18} /> Save Configuration
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PerformanceOptimization;
